import { ImageServiceType, parseImageUrl } from '@omni/kit/utilities/utilities';
import { Keyboard, Platform, View } from 'react-native';
import {
  KitIcon,
  KitSnack,
  KitTouchable,
  KitTouchableIcon,
} from '@omni/kit/components';
import React, { useEffect, useState } from 'react';
import {
  channelSelector,
  pushPreferenceSelector,
} from '../../shared/redux/selectors';
import {
  getPushPreference,
  setPushPreference,
} from '../../shared/redux/actions/SystemActions';
import { useDispatch, useSelector } from 'react-redux';

import BorderRadius from '@omni/kit/theming/BorderRadius';
import { CHANNEL_DETAILS_MODAL } from '../../shared/Constants';
import { ChannelType } from '../../shared/redux/types';
import Colors from '@omni/kit/theming/Colors';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import KitImage from '@omni/kit/components/KitImage';
import { KitSnackDuration } from '@omni/kit/components/KitSnack';
import Spacing from '@omni/kit/theming/Spacing';
import { useNavigation } from '@react-navigation/native';
import { PushTriggerOption } from '@sendbird/chat';

export default function ChatRightNavButtons(): JSX.Element {
  const [isMuted, setIsMuted] = useState(false);
  const channel = useSelector(channelSelector);
  const pushPreference = useSelector(pushPreferenceSelector);

  const dispatch = useDispatch();
  const navigation = useNavigation();

  const _onNavDetailsButtonPress = () => {
    Keyboard.dismiss();
    navigation.navigate(CHANNEL_DETAILS_MODAL, { channel });
  };

  const _handleMuteChannel = () => {
    const preference = isMuted
      ? PushTriggerOption.ALL
      : PushTriggerOption.MENTION_ONLY;
    setIsMuted(!isMuted);
    dispatch(setPushPreference(preference, (channel as GroupChannel).url));
    KitSnack.show(
      isMuted ? 'Group unmuted' : 'Group muted',
      KitSnackDuration.SHORT
    );
  };

  const _renderAvatar = () => {
    if (channel?.customType === ChannelType.Direct) {
      return (
        <KitTouchableIcon
          name='action-h'
          onPress={_onNavDetailsButtonPress}
          buttonSize={35}
          size={22}
        />
      );
    }

    const avatarSize = 35;
    let avatarComponent: JSX.Element | null = null;
    let icon = false;

    if (
      channel?.coverUrl?.startsWith('https://images.subsplash.com') ||
      channel?.coverUrl?.startsWith('https://images.stage.subsplash.net')
    ) {
      const channelImage = parseImageUrl(
        channel.coverUrl,
        avatarSize,
        avatarSize,
        ImageServiceType.ImageJpeg
      );
      avatarComponent = (
        <KitImage
          source={{
            uri: channelImage,
          }}
          style={{
            borderRadius: BorderRadius.xl,
            height: avatarSize,
            opacity: 1,
            width: avatarSize,
          }}
          resizeMode='cover'
        />
      );
    } else {
      icon = true;
      avatarComponent = <KitIcon name='channel' size={22} color={Colors.N0} />;
    }

    return (
      <KitTouchable
        onPress={_onNavDetailsButtonPress}
        underlayColor={icon ? Colors.N300 : Colors.N900}
        style={{
          alignItems: 'center',
          backgroundColor: Colors.N200,
          borderRadius: 40,
          height: avatarSize,
          justifyContent: 'center',
          width: avatarSize,
          ...Platform.select({ web: { marginRight: Spacing.l } }),
        }}
      >
        {avatarComponent}
      </KitTouchable>
    );
  };

  useEffect(() => {
    if (channel) dispatch(getPushPreference(channel.url));
  }, [channel?.url, dispatch]);

  useEffect(() => {
    const muted = pushPreference === 'mention_only';
    setIsMuted(muted);
  }, [pushPreference]);

  return (
    <View style={{ flexDirection: 'row' }}>
      <KitTouchableIcon
        name={isMuted ? 'notifications-off' : 'notifications'}
        onPress={_handleMuteChannel}
        buttonSize={35}
        size={20}
      />
      <View style={{ width: Spacing.m }} />
      {_renderAvatar()}
    </View>
  );
}
